import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

const CMSProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    sku: '',
    brand_id: '',
    category_id: '',
    short_description: '',
    long_description: '',
    price: '',
    discount: '',
    stock_quantity: '0',
    is_active: true,
    is_featured: false,
    status: 'draft',
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [primaryImageId, setPrimaryImageId] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || '';
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${apiBase.replace(/\/api\/?$/, '')}${url}`;
  };

  useEffect(() => {
    (async () => {
      const [brandRes, catRes] = await Promise.all([
        api.getEcommerceBrands(),
        api.getEcommerceCategories(),
      ]);
      if (brandRes.success) setBrands(brandRes.data || []);
      if (catRes.success) setCategories(catRes.data || []);
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.getEcommerceProductById(id);
        if (res.success && res.data) {
          const p = res.data;
          setForm({
            name: p.name || '',
            sku: p.sku || '',
            brand_id: p.brand_id || '',
            category_id: p.category_id || '',
            short_description: p.short_description || '',
            long_description: p.long_description || '',
            price: p.price != null ? String(p.price) : '',
            discount: p.discount != null ? String(p.discount) : '',
            stock_quantity: String(p.stock_quantity ?? 0),
            is_active: Boolean(p.is_active),
            is_featured: Boolean(p.is_featured),
            status: p.status || 'draft',
          });
          setExistingImages(p.images || []);
          setPrimaryImageId(p.primary_image_id || (p.images && p.images[0] && p.images[0].id));
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const setPrimaryExisting = async (imageId) => {
    if (!id) return;
    try {
      await api.setEcommerceProductPrimaryImage(id, imageId);
      setPrimaryImageId(imageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const removeExistingImage = async (imageId) => {
    if (!id || !window.confirm('Remove this image?')) return;
    try {
      await api.deleteEcommerceProductImage(id, imageId);
      setExistingImages((prev) => prev.filter((i) => i.id !== imageId));
      if (primaryImageId === imageId) {
        const remaining = existingImages.filter((i) => i.id !== imageId);
        if (remaining[0]) await api.setEcommerceProductPrimaryImage(id, remaining[0].id);
        setPrimaryImageId(remaining[0] ? remaining[0].id : null);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('sku', form.sku);
    fd.append('brand_id', form.brand_id || '');
    fd.append('category_id', form.category_id || '');
    fd.append('short_description', form.short_description);
    fd.append('long_description', form.long_description);
    fd.append('price', form.price || '0');
    fd.append('discount', form.discount || '');
    fd.append('stock_quantity', form.stock_quantity || '0');
    fd.append('is_active', form.is_active ? 'true' : 'false');
    fd.append('is_featured', form.is_featured ? 'true' : 'false');
    fd.append('status', form.status);
    imageFiles.forEach((file) => fd.append('images', file));
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const formData = buildFormData();
      if (isEdit) {
        const res = await api.updateEcommerceProduct(id, formData);
        if (res.success) {
          navigate('/cms/products');
          return;
        }
      } else {
        const res = await api.createEcommerceProduct(formData);
        if (res.success) {
          navigate('/cms/products');
          return;
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">{isEdit ? 'Edit Product' : 'New Product'}</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SKU *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="sku"
                    value={form.sku}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Brand</label>
                      <select
                        className="form-control"
                        name="brand_id"
                        value={form.brand_id}
                        onChange={handleChange}
                      >
                        <option value="">— Select —</option>
                        {brands.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        className="form-control"
                        name="category_id"
                        value={form.category_id}
                        onChange={handleChange}
                      >
                        <option value="">— Select —</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.parent_name ? `${c.parent_name} › ` : ''}{c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Short description</label>
                  <textarea
                    className="form-control"
                    name="short_description"
                    rows="2"
                    value={form.short_description}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Long description</label>
                  <textarea
                    className="form-control"
                    name="long_description"
                    rows="8"
                    value={form.long_description}
                    onChange={handleChange}
                  />
                  <small className="form-text text-muted">Rich text can be added in a future update.</small>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Price *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        name="price"
                        value={form.price}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Discount</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        name="discount"
                        value={form.discount}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Stock quantity</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        name="stock_quantity"
                        value={form.stock_quantity}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Publish</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    className="form-control"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="form-group">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="is_active"
                      name="is_active"
                      checked={form.is_active}
                      onChange={handleChange}
                    />
                    <label className="custom-control-label" htmlFor="is_active">Active</label>
                  </div>
                </div>
                <div className="form-group">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="is_featured"
                      name="is_featured"
                      checked={form.is_featured}
                      onChange={handleChange}
                    />
                    <label className="custom-control-label" htmlFor="is_featured">Featured</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Product images</h3>
              </div>
              <div className="card-body">
                {existingImages.length > 0 && (
                  <div className="mb-2">
                    <small className="text-muted">Current images (click star to set primary)</small>
                    <div className="d-flex flex-wrap mt-1">
                      {existingImages.map((img) => (
                        <div key={img.id} className="position-relative mr-2 mb-2">
                          <img
                            src={getImageUrl(img.image_url)}
                            alt=""
                            style={{ width: 60, height: 60, objectFit: 'cover', border: primaryImageId === img.id ? '2px solid #007bff' : '1px solid #ddd' }}
                          />
                          <button
                            type="button"
                            className="btn btn-xs btn-link p-0 position-absolute"
                            style={{ top: -4, left: -4 }}
                            onClick={() => setPrimaryExisting(img.id)}
                            title="Set primary"
                          >
                            <i className={`fas fa-star ${primaryImageId === img.id ? 'text-warning' : 'text-muted'}`}></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-danger position-absolute"
                            style={{ top: -4, right: -4 }}
                            onClick={() => removeExistingImage(img.id)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <label className="d-block">Add new images</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="form-control-file"
                  onChange={handleImageSelect}
                />
                {imageFiles.length > 0 && (
                  <div className="d-flex flex-wrap mt-2">
                    {imageFiles.map((file, idx) => (
                      <div key={idx} className="mr-2 mb-2 position-relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt=""
                          style={{ width: 60, height: 60, objectFit: 'cover' }}
                        />
                        <button
                          type="button"
                          className="btn btn-xs btn-danger position-absolute"
                          style={{ top: -4, right: -4 }}
                          onClick={() => removeNewImage(idx)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update Product' : 'Create Product')}
            </button>
            <Link to="/cms/products" className="btn btn-default ml-2">Cancel</Link>
          </div>
        </div>
      </form>
    </>
  );
};

export default CMSProductForm;
